import { action, makeObservable, observable, runInAction } from 'mobx';
import { OTP_MODE, SESSION_EXPIRED_TIME_MS, USER_ACCESS_TOKEN, USER_LOGGED_IN_TIME } from 'src/constants';
import { ResponseDTO } from 'src/dto/base.dto';
import { CheckEmailExistingDTO, LogInDTO, LoginResponse } from 'src/dto/login.dto';
import { IHttpService } from 'src/services/http.service';
import { IUserStore, IUser } from 'src/store/user';
import { HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import { IResponseResendOTP, OtpDTO, OtpVerificationResponse, ResendOTP } from 'src/dto/otp.dto';
import { LogoutDTO } from 'src/dto/logout.dto';
import eventEmitter from '../event';
import {
	ChangeForgottenPasswordDTO,
	ForgotPasswordDTO,
	IChangeForgottenPasswordBody,
	IForgotPasswordBody
} from 'src/dto/forgot-password.dto';
import moment from 'moment';
import { IOnboardingStore } from '../user/onboarding.store';

export interface IPreLoginLocation {
	path: string;
	search?: string;
}

let timer: number | undefined = undefined;
export interface IAuthStore {
	isAuthenticated: boolean;
	otpToken?: string | null;
	otpMode?: OTP_MODE;
	isVerified: boolean;
	signedPUD?: boolean;
	isSessionExpired?: boolean;
	preLoginData?: IPreLoginLocation | null;
	login(loginForm: LogInDTO): Promise<boolean>;
	logout(): Promise<void>;
	verifyOTP(otpCode: string): Promise<boolean>;
	resendOTP(): Promise<boolean>;
	setAuthenticated(isAuthenticated: boolean): void;
	setOtpToken(token?: string): void;
	forgotPassword(dto: IForgotPasswordBody): Promise<object>;
	changeForgottenPassword(dto: IChangeForgottenPasswordBody, token: string): Promise<boolean>;
	setOtpMode(mode: OTP_MODE): void;
	setPreAuthLocation(log: IPreLoginLocation): void;
	checkEmailExisting(email: string): Promise<boolean>;
	dispose(): void;
	otpExpiredAt?: Date | null;
	setOtpExpiredAt(otpExpiredAt?: Date): void;
	resetTimeout(): void;
}
export class AuthStore implements IAuthStore {
	@observable
	isAuthenticated = false;

	@observable
	otpToken?: string | null = null;

	@observable
	isVerified = false;

	@observable
	otpMode: OTP_MODE = OTP_MODE.LOGIN;

	@observable.deep
	preLoginData?: IPreLoginLocation | null;

	@observable
	signedPUD?: boolean = false;

	@observable
	otpExpiredAt?: Date | null = null;

	@observable
	isSessionExpired?: boolean = false;

	constructor(private readonly http: IHttpService, private userStore: IUserStore, private onboardingStore: IOnboardingStore) {
		const localToken = localStorage.getItem(USER_ACCESS_TOKEN);
		if (localToken) {
			const time = localStorage.getItem(USER_LOGGED_IN_TIME);
			if (time) {
				const remainingTime = moment(time).diff(moment(), 'milliseconds') + SESSION_EXPIRED_TIME_MS;
				if (remainingTime > 0) {
					timer = setTimeout(() => {
						this.isSessionExpired = true;
					}, remainingTime) as unknown as number;
				} else {
					this.logout();
				}
			}
		}
		this.isAuthenticated = !!localToken;
		this.isVerified = !!localToken;
		makeObservable(this);
	}

	@action.bound
	setAuthenticated(isAuthenticated: boolean): void {
		this.isAuthenticated = isAuthenticated;
	}

	@action.bound
	setOtpToken(token: string): void {
		this.otpToken = token;
	}

	@action.bound
	setIsVerified(isVerified: boolean): void {
		this.isVerified = isVerified;
	}

	@action.bound
	public setOtpMode(mode: OTP_MODE) {
		this.otpMode = mode;
	}

	@action.bound
	setPreAuthLocation(log: IPreLoginLocation | null): void {
		this.preLoginData = log;
	}

	@action.bound
	setOtpExpiredAt(otpExpiredAt: Date): void {
		this.otpExpiredAt = otpExpiredAt;
	}

	public async login(loginForm: LogInDTO): Promise<boolean> {
		const loginRes: ResponseDTO<LoginResponse> = await this.http.request<LogInDTO, LoginResponse>(loginForm);
		if (loginRes.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.isAuthenticated = true;
				this.otpToken = loginRes.data?.token;
				this.otpExpiredAt = loginRes.data?.otpExpiredAt;
				this.signedPUD = loginRes.data?.signedPUD;
			});
			return true;
		}
		return false;
	}

	public async logout(): Promise<void> {
		const logoutForm = new LogoutDTO();
		await this.http.request<LogoutDTO, String>(logoutForm);
		eventEmitter.emit('logout');
		this.http.setToken(undefined);

		if (timer) {
			clearTimeout(timer);
		}

		runInAction(() => {
			this.setAuthenticated(false);
			this.isVerified = false;
			this.isSessionExpired = false;
			this.userStore.removeUser();
			this.onboardingStore.setFunderOnboardingRequest(undefined);
			localStorage.removeItem(USER_ACCESS_TOKEN);
			localStorage.removeItem(USER_LOGGED_IN_TIME);
		});
	}

	public async verifyOTP(otpCode: string): Promise<boolean> {
		const otpForm = new OtpDTO(
			{
				code: otpCode
			},
			{
				Authorization: 'Bearer ' + this.otpToken
			}
		);

		const otpRes: ResponseDTO<OtpVerificationResponse> = await this.http.request<OtpDTO, OtpVerificationResponse>(
			otpForm
		);
		if (otpRes.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			const data = otpRes.data;
			runInAction(() => {
				this.isAuthenticated = true;
				this.otpToken = data?.token;
				this.setIsVerified(true);
			});
			this.http.setToken(data?.token);
			localStorage.setItem(USER_LOGGED_IN_TIME, new Date().toISOString());
			this.userStore.setUser({
				...data
			} as IUser);

			timer = setTimeout(() => {
				this.isSessionExpired = true;
			}, SESSION_EXPIRED_TIME_MS) as unknown as number;

			return true;
		}
		return false;
	}

	public async resendOTP(): Promise<boolean> {
		let otpMode: { forRegister: OTP_MODE } | undefined = undefined;
		if (this.otpMode === OTP_MODE.REGISTER) {
			otpMode = {
				forRegister: OTP_MODE.REGISTER
			};
		}
		const resendOTPForm = new ResendOTP(
			{
				Authorization: 'Bearer ' + this.otpToken
			},
			otpMode
		);
		const rs: ResponseDTO<IResponseResendOTP> = await this.http.request<ResendOTP, IResponseResendOTP>(resendOTPForm);

		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.otpExpiredAt = rs.data?.otpExpiredAt;
			});
			return true;
		}

		return false;
	}

	public async forgotPassword(dto: IForgotPasswordBody): Promise<object> {
		const forgotPasswordDTO = new ForgotPasswordDTO(dto);

		const otpRes: ResponseDTO<String> = await this.http.request<ForgotPasswordDTO, String>(forgotPasswordDTO);

		if (otpRes.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			return {
				status: true,
				...otpRes
			};
		}
		return {
			status: false,
			...otpRes
		};
	}

	public async changeForgottenPassword(dto: IChangeForgottenPasswordBody, token: string): Promise<boolean> {
		const changeForgottenPasswordDTO = new ChangeForgottenPasswordDTO(dto, {
			Authorization: 'Bearer ' + token
		});
		const changeForgottenPasswordRes: ResponseDTO<OtpVerificationResponse> = await this.http.request<
			ChangeForgottenPasswordDTO,
			OtpVerificationResponse
		>(changeForgottenPasswordDTO);
		if (changeForgottenPasswordRes.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			const data = changeForgottenPasswordRes.data;
			runInAction(() => {
				this.isAuthenticated = true;
			});
			this.http.setToken(data?.token);
			this.userStore.setUser({
				...data
			} as IUser);

			timer = setTimeout(() => {
				this.isSessionExpired = true;
			}, SESSION_EXPIRED_TIME_MS) as unknown as number;

			return true;
		}
		return false;
	}
	public async checkEmailExisting(email: string): Promise<boolean> {
		const emailDTO = new CheckEmailExistingDTO({
			email
		});

		const res: ResponseDTO<boolean> = await this.http.request<CheckEmailExistingDTO, boolean>(emailDTO);
		return !!res.data && res.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS;
	}

	@action.bound
	public dispose(): void {
		this.isAuthenticated = false;
		this.isVerified = false;
		this.otpToken = undefined;
	}

	public resetTimeout(): void {
		clearTimeout(timer);
		this.isSessionExpired = false;
		localStorage.setItem(USER_LOGGED_IN_TIME, new Date().toISOString());
		timer = setTimeout(() => {
			this.isSessionExpired = true;
		}, SESSION_EXPIRED_TIME_MS) as unknown as number;
	}
}
